import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import ShipeeziGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import {
  DespatchProduct,
  DespatchShipmentContainer as ContainerProduct,
} from "../../../../../../../models/despatch";
import { Product } from "../../../../../../../models/product";
import { Searches } from "../../../../../../../models/shipeezi.table";
import { Warehouse } from "../../../../../../../models/warehouse";
import useAction from "../../hooks/useAction";
import StatusBadge from "../StatusBadge";
import { GridContainer, ProductModal } from "./styles";

export interface AddNewProductHandles {
  open: (productIdsSelected: number[]) => void;
  close: () => void;
}
interface AddNewProductProps {
  onAddProducts: (selectedProducts: DespatchProduct[]) => void;
  onCancel?: () => void;
}

const ModalAddProduct: ForwardRefRenderFunction<AddNewProductHandles, AddNewProductProps> = (
  props,
  ref
) => {
  const { onAddProducts } = props;

  const { t: translate } = useTranslation();
  const { getAvailableProducts } = useAction();

  const [modalOpened, setModalOpened] = useState<boolean>(false);

  const [availableProducts, setAvailableProducts] = useState<ContainerProduct[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);

  const [addedProducts, setAddedProducts] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const { isLoading, data: productsData } = getAvailableProducts(page, pageSize, searches);

  useEffect(() => {
    if (productsData) {
      setAvailableProducts(productsData.rows);
      setTotalItems(productsData.count);
    }
  }, [productsData]);

  const openModal = useCallback((productIdsSelected: number[]) => {
    setAddedProducts(productIdsSelected);
    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpened(false);
    setSelectedProducts([]);
    setAddedProducts([]);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const displaySelectedProducts = () => {
    if (addedProducts?.length) {
      return selectedProducts.concat(addedProducts);
    }

    return selectedProducts;
  };

  const onCallAddProducts = () => {
    const selectedDespatchProducts: DespatchProduct[] = [];

    for (const containerProductId of selectedProducts) {
      if (addedProducts && !addedProducts.includes(containerProductId)) {
        const containerProduct = availableProducts.find(
          (containerProduct) => containerProduct.id === containerProductId
        );

        if (containerProduct && containerProduct.id) {
          const despatchProduct: DespatchProduct = {
            containerId: containerProduct?.containerId,
            purchaseOrderId: containerProduct?.purchaseOrder || "",
            productId: containerProduct?.id,
            container: containerProduct,
            companyId: containerProduct?.companyId,
            productQuantity: 0,
            palletsQuantity: 0,
            quantityOnHand: containerProduct?.quantityOnHand,
          };

          selectedDespatchProducts.push(despatchProduct);
        }
      }
    }

    onAddProducts(selectedDespatchProducts);
    closeModal();
  };

  const columns: Column[] = [
    {
      name: "product",
      title: translate("sku"),
      formatText: (product?: Product) => product?.sku,
      searchIdentifier: "product.sku",
    },
    {
      name: "product",
      title: translate("productName"),
      formatText: (product?: Product) => product?.description,
      searchIdentifier: "product.description",
    },
    {
      name: "purchaseOrder",
      title: translate("purchaseOrder"),
      searchIdentifier: "purchase_order",
    },
    { name: "containerId", title: translate("container"), searchIdentifier: "container_id" },
    {
      name: "warehouse",
      title: translate("warehouse"),
      formatText: (warehouse: Warehouse) => warehouse?.name,
      searchIdentifier: "warehouse.name",
    },
    {
      name: "quantityOnHand",
      title: translate("quantityOnHand"),
      hideSearch: true,
      formatText: (value?: number) => value || 0,
    },
    {
      name: "status",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (value?: string) => <StatusBadge value={value} />,
    },
  ];

  const handleSetPage = (page: number) => {
    setPage(page - 1);
  };

  const handleSearch = (searches: Searches) => {
    setPage(0);
    setSearches(searches);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      inputPlaceholder: translate("typePressEnter"),
      inputSearchMode: "enter",
      onSearch: handleSearch,
    },
  };

  return (
    <ProductModal
      open={modalOpened}
      handleClose={closeModal}
      showModalFooter
      showActionButtons
      actionButtonProps={{
        textSave: translate("addProducts"),
        saveDisabled: selectedProducts.length === 0,
        showSaveButton: true,
        showCancelButton: true,
        onSave: onCallAddProducts,
        onCancel: closeModal,
      }}
      title={translate("products")}
      data-cy="despatch-products-add-product-modal"
    >
      <GridContainer data-cy="despatch-products-modal-container">
        <ShipeeziGrid
          data={availableProducts}
          totalItems={totalItems}
          columns={columns}
          loadingData={isLoading}
          emptyData={translate("noInformationToBeShown")}
          selecteds={displaySelectedProducts()}
          handleSelect={setSelectedProducts}
          pageSize={pageSize}
          currentPage={page}
          pageSizeList={[10, 30, 50, 100]}
          onChangePageSize={setPageSize}
          onNextPage={handleSetPage}
          onPreviousPage={handleSetPage}
          onGoToPage={handleSetPage}
          generalActions={generalActions}
        />
      </GridContainer>
    </ProductModal>
  );
};

export default forwardRef(ModalAddProduct);
